import moment from "moment"
import * as turf from "@turf/turf"
import { MapboxStyle } from '@/lib/extension'
import MVTImageryProvider from 'mvt-imagery-provider'
import { CallbackProperty } from "cesium";

export default class DayNightLineLayer {

    constructor() {
        this.twilightWidth = 667918;
		this.twilightSteps = 15;
		this.points = 288;

        const topRight = turf.toMercator(turf.point([180, 90]));
		this.maxDimension = topRight.geometry.coordinates[1]

        let bottomLeft = turf.toMercator(turf.point([-180, -90]))
		this.minDimension = bottomLeft.geometry.coordinates[1]
        this.terminatorCurveSetCache = {}
    }

    // 未完
    addTo(viewer) {
		this.viewer = viewer
		const style = this._getStyle()
		if(!this.mapboxLayer) {
			this.mapboxLayer = this.viewer.imageryLayers.addImageryProvider(new MVTImageryProvider({
				style: style
			}))
		}

		/**this.timer = setInterval(() => {
			this.refresh()
		}, 10 * 1000)**/
    }

	refresh() {
		const style = this._getStyle()
		if(this.mapboxLayer) {
			this.viewer.imageryLayers.remove(this.mapboxLayer, true)
		}
		this.mapboxLayer = this.viewer.imageryLayers.addImageryProvider(new MVTImageryProvider({
			style: style
		}))
	}

	_getStyle() {
		const time = new Date().getTime()/1000
		const geojson = this.setClock(time)
		let style = new MapboxStyle()
		style.setSources({
			"dayline-source": {
				type: "geojson",
				data: geojson
			}
		})
		style.setLayers([
			{
				"id": "dayline-layer",
				"source": "dayline-source",
				"type": "fill",
				"paint": {
					"fill-color": "rgba(0, 0, 0, 0.05)",
					"fill-outline-color": "rgba(0, 0, 0, 0)"
				}
			}
		])
		return style.getStyleJSON()
	}

	remove() {
		if(this.mapboxLayer) {
			this.viewer.imageryLayers.remove(this.mapboxLayer, true)
		}
		if(this.timer) {
			clearInterval(this.timer)
		}
	}

    setClock(clock) {
        let nightFeatures = []
        for (var i = 0; i < this.twilightSteps; i++) {
			nightFeatures.push({
				type: 'Feature',
				properties: {
					type: 'night'
				},
				geometry: {
					type: 'Polygon',
					coordinates: []
				}
			});
		}
        const now = moment.unix(clock).utc()
        let baseCurveData = this.getTerminatorCurveSet(now.dayOfYear());
		let dayFraction = (now.hour() * 3600 + now.minute() * 60 + now.second()) / 86400;
		let offsetX = dayFraction * 2 * this.maxDimension;
		offsetX = Math.round(offsetX / ((2 * this.maxDimension) / this.points)) * ((2 * this.maxDimension) / this.points);
		for(let i=0;i<baseCurveData.curves.length; i++) {
			let curve = baseCurveData.curves[i]
			for(let j =0;j<curve.length;j++) {
				curve[j][0] -= offsetX; 
			}
			let count = 0;
			// eslint-disable-next-line no-constant-condition
			while(true) {
				if (count > curve.length) {
					break;
				}
				if (curve[0][0] < this.minDimension) {
					let coord = curve.shift();
					coord[0] += (this.maxDimension - this.minDimension);
					curve.push(coord);
				} else {
					break;
				}
				count++;
			}
			curve.push([curve[0][0] + (this.maxDimension - this.minDimension), curve[0][1]]);
			let nightCoords = curve.slice(0);
			nightCoords.push([this.maxDimension, baseCurveData.nightShimCoord], [this.minDimension, baseCurveData.nightShimCoord], curve[0]);
			for(let n=0;n<nightCoords.length;n++) {
				let transformPoint = turf.toWgs84(turf.point(nightCoords[n]))
				nightCoords[n] = transformPoint.geometry.coordinates
			}
			nightFeatures[i].geometry.coordinates.push(nightCoords);
		}

        let geojson = {
			type:'FeatureCollection',
			features: nightFeatures
		}

        return geojson
    }

    // 缓存中获取
    getTerminatorCurveSet(dayOfYear) {
        if(!(dayOfYear in this.terminatorCurveSetCache)) {
			this.terminatorCurveSetCache[dayOfYear] = this.generateTerminatorCurveSet(dayOfYear)
		}
		return Object.assign({}, this.terminatorCurveSetCache[dayOfYear])
    }

    // 生成夜间面
    generateTerminatorCurveSet(dayOfYear) {
		let that = this
        const offsetX = this.maxDimension
		let declination = 0.40927971 * Math.sin((2 * Math.PI / 365) * (dayOfYear - 81.5))
		let termFunction = function(lon) {
			var cosFactor = -Math.cos((lon + offsetX) * (Math.PI / that.maxDimension))
			return (2 * that.maxDimension / Math.PI) * Math.atan(cosFactor / Math.tan(declination))
		}
        let lonPrimeFunction = function(t) {
			return (that.maxDimension - that.minDimension) / that.points
		}
		let latPrimeFunction = function(t) {
            let aFactor = 2 * that.maxDimension / Math.PI
            let bFactor = offsetX
            let cFactor = Math.PI / that.maxDimension
            let dFactor = Math.tan(declination)
            let cosOperand = ((that.minDimension + (((that.maxDimension - that.minDimension) / that.points) * t)) + bFactor) * cFactor
			return (aFactor / (1 + Math.pow((-Math.cos(cosOperand) / dFactor), 2))) * (Math.sin(cosOperand) / dFactor) * (cFactor * (that.maxDimension - that.minDimension)) / that.points
		}
		let lonParallelFunction = function(dist, t) {
			return (dist * latPrimeFunction(t)) / Math.sqrt(Math.pow(lonPrimeFunction(t), 2) + Math.pow(latPrimeFunction(t), 2))
		}
		let latParallelFunction = function(dist, t) {
			return (dist * lonPrimeFunction(t)) / Math.sqrt(Math.pow(lonPrimeFunction(t), 2) + Math.pow(latPrimeFunction(t), 2))
		}
		let lineCoords = []
		for (let i = 0; i < this.twilightSteps; i++) {
			lineCoords.push([])
		}
		for (let i = 0; i < this.points; i++) {
			let lon = this.minDimension + ((this.maxDimension - this.minDimension) / this.points * i)
			let lat = termFunction(lon)
			lineCoords[0].push([lon, lat])
			let latDeg = turf.toWgs84(turf.point([lon, lat])).geometry.coordinates[1]
			let latRad = latDeg * Math.PI / 180
			let baseDist = (this.twilightWidth / (this.twilightSteps - 1)) / Math.cos(latRad)
			let steps = (this.twilightSteps - 1) / 2
			for (let j = 1; j <= steps; j++) {
				var dist = baseDist * j
				lineCoords[j].push([lon + lonParallelFunction(dist, i), Math.max(lat - latParallelFunction(dist, i), this.minDimension)])
				lineCoords[j + steps].push([lon - lonParallelFunction(dist, i), Math.min(lat + latParallelFunction(dist, i), this.maxDimension)])
			}
		}
		var dayShimCoord = (declination < 0) ? this.minDimension : this.maxDimension
		var nightShimCoord = (declination < 0) ? this.maxDimension : this.minDimension
		return {
			curves: lineCoords,
			dayShimCoord: dayShimCoord,
			nightShimCoord: nightShimCoord
		};
    }
}